import React, { useState, useEffect, useRef } from 'react';
import { HeroImage as HeroImageType } from './types';
import { MAX_VEHICLE_HERO_IMAGES } from '../../constants/vehicleGallery';
import { HeroImage } from '../ui/HeroImage';
import { focalToObjectPosition } from '../../constants/imageCropPresets';

interface MobileImageCarouselProps {
  images: HeroImageType[];
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
  onAddImage: () => void;
  /** Larger heights for tablet / iPad kiosk displays */
  size?: 'default' | 'tablet' | 'showcase';
  /** Keep slideshow running (default true for visitor / kiosk views) */
  autoPlay?: boolean;
}

const carouselHeightClass: Record<NonNullable<MobileImageCarouselProps['size']>, string> = {
  default: 'h-[52vw] max-h-80 min-h-[14rem] sm:h-80',
  tablet: 'h-72 sm:h-80 md:h-[42vh] md:min-h-[320px] md:max-h-[520px]',
  showcase:
    'h-[38vh] min-h-[280px] sm:min-h-[320px] md:min-h-[360px] lg:min-h-0 lg:h-full',
};

export const MobileImageCarousel: React.FC<MobileImageCarouselProps> = ({
  images,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
  onAddImage,
  size = 'default',
  autoPlay = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const pauseAutoPlayTemporarily = () => {
    setIsAutoPlaying(false);

    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
    }

    pauseTimerRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
      pauseTimerRef.current = null;
    }, 5000);
  };

  useEffect(() => {
    setIsAutoPlaying(autoPlay);
  }, [autoPlay]);

  useEffect(() => {
    if (isAutoPlaying && images.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 4000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
      }
    };
  }, [isAutoPlaying, images.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    pauseAutoPlayTemporarily();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    pauseAutoPlayTemporarily();
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    pauseAutoPlayTemporarily();
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    pauseAutoPlayTemporarily();
  };

  if (images.length === 0) {
    return (
      <div
        className={`build-photo-empty relative ${carouselHeightClass[size]} bg-[#141414]/30 backdrop-blur-sm rounded-xl border border-white/[0.08] flex items-center justify-center`}
      >
        {isOwner ? (
          <button
            onClick={onAddImage}
            className="flex flex-col items-center space-y-2 text-gray-400 hover:text-orange-400 transition-colors"
          >
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-sm font-medium">Add Your First Photo</span>
          </button>
        ) : (
          <div className="text-center">
            <svg className="w-12 h-12 text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-sm">No photos available</p>
          </div>
        )}
      </div>
    );
  }

  const navButtonClass =
    size === 'showcase'
      ? 'absolute top-1/2 transform -translate-y-1/2 p-3.5 bg-black/55 backdrop-blur-sm hover:bg-black/75 text-white rounded-full transition-all showcase-touch-target'
      : 'absolute top-1/2 transform -translate-y-1/2 p-2.5 md:p-3 bg-black/50 backdrop-blur-sm hover:bg-black/70 text-white rounded-full transition-all';

  const dotClass = (active: boolean) =>
    size === 'showcase'
      ? `h-2.5 rounded-full transition-all duration-300 ${active ? 'bg-orange-400 w-8' : 'bg-[#6b6b6b] w-2.5'}`
      : `w-2 h-2 rounded-full transition-all duration-300 ${
          active ? 'bg-orange-400 w-6' : 'bg-[#6b6b6b] hover:bg-gray-400'
        }`;

  return (
    <div className={`relative ${size === 'showcase' ? 'h-full flex flex-col' : ''}`}>
      <div
        ref={carouselRef}
        className={`relative ${carouselHeightClass[size]} bg-[#0a0a0a] rounded-xl overflow-hidden border border-white/[0.08] ${size === 'showcase' ? 'flex-1' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="relative w-full h-full flex-shrink-0 cursor-pointer"
              onClick={() => onImageClick(index)}
            >
              <HeroImage
                src={image.src}
                alt={image.alt}
                position={focalToObjectPosition(image.focalX, image.focalY)}
                focalX={image.focalX}
                focalY={image.focalY}
                zoom={image.zoom}
                priority={index === 0}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-4">
                <h3
                  className={`text-white font-medium truncate backdrop-blur-sm bg-black/30 px-2 py-1 rounded ${
                    size === 'showcase' ? 'text-base sm:text-lg px-3 py-1.5' : 'text-sm'
                  }`}
                >
                  {image.title}
                </h3>
              </div>

              {isOwner && (
                <div className="absolute top-3 right-3 flex space-x-1">
                  {index > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveImage(index, index - 1);
                      }}
                      className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30"
                      title="Move left"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  {index < images.length - 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveImage(index, index + 1);
                      }}
                      className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30"
                      title="Move right"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditImage(index);
                    }}
                    className="p-1.5 bg-[#141414]/80 backdrop-blur-sm hover:bg-white/[0.06] text-white rounded-lg transition-colors border border-white/[0.08]"
                    title="Edit image"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDeleteImage(index);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="p-1.5 bg-red-500/80 backdrop-blur-sm hover:bg-red-500 text-white rounded-lg transition-colors border border-red-400/30 z-10"
                    title="Delete image"
                    type="button"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className={`${navButtonClass} left-3 md:left-4`}
              type="button"
              aria-label="Previous photo"
            >
              <svg className={size === 'showcase' ? 'w-6 h-6' : 'w-4 h-4 md:w-5 md:h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className={`${navButtonClass} right-3 md:right-4`}
              type="button"
              aria-label="Next photo"
            >
              <svg className={size === 'showcase' ? 'w-6 h-6' : 'w-4 h-4 md:w-5 md:h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {isAutoPlaying && images.length > 1 && (
          <div className="absolute top-3 left-3">
            <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              <span className="text-white text-xs">Auto</span>
            </div>
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className={`flex justify-center space-x-2.5 ${size === 'showcase' ? 'mt-4' : 'mt-3'}`}>
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={dotClass(index === currentIndex)}
              type="button"
              aria-label={`Go to photo ${index + 1}`}
            />
          ))}
        </div>
      )}

      {isOwner && images.length < MAX_VEHICLE_HERO_IMAGES && (
        <button
          type="button"
          onClick={onAddImage}
          className={`${images.length === 0 ? 'btn-primary' : 'btn-secondary'} mt-3 w-full`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>
            {images.length === 0
              ? 'Add photos'
              : `Add photo (${images.length}/${MAX_VEHICLE_HERO_IMAGES})`}
          </span>
        </button>
      )}
    </div>
  );
};
